/* eslint-disable */
/**
 * KpiKort — Venstre-kant = statusfarve, primærtal ALTID hvidt (#f0f4f8)
 *
 * Props:
 *   label       string      — øverst lille tekst
 *   kortLabel   string      — valgfri forkortet mobil-label (fx "NYE/UGE").
 *                             Vises i stedet for label på mobil.
 *                             Hvis ikke sat, wrappes label pænt på 2 linjer på mobil.
 *   value       any         — stort tal/tekst (null/undefined → —)
 *   farve       string      — hex-farve til VENSTRE KANT og delta (default: STATUS.konfigureret)
 *   ikon        ReactNode   — Lucide-ikon-node (fx <Banknote size={16}/>) ELLER streng (fallback)
 *   delta       number      — positiv = grøn pil op, negativ = rød pil ned, 0/null = ingen pil
 *   deltaLabel  string      — tekst ved siden af delta (fx "vs. forrige uge")
 *   onClick     fn          — gør kortet klikbart
 *   badge       string      — lille badge i tophjørne (LIVE | KONFIGURERET | IKKE FORBUNDET)
 *   style       obj         — ekstra inline styles
 *   loading     bool        — viser skeleton-animation
 *   mobil       bool        — mobil-tilstand: kompakt layout, ingen hover-transform, kortere badge-tekst
 */

import React from 'react';
import { BG, KANT, TEKST, STATUS, BADGE_FV, RADIUS, FONT, SPACING, TYPE } from './tokens';

// Badge-tekst-map: lange badge-labels → korte mobil-varianter
const BADGE_MOBIL = {
  'KONFIGURERET':     'KONF.',
  'IKKE FORBUNDET':   'OFFLINE',
  'OPMÆRKSOMHED':     'OBS',
  'KRÆVER DINERO':    'DINERO',
};

export default function KpiKort({ label, kortLabel, value, farve, ikon, delta, deltaLabel, onClick, badge, style = {}, loading = false, mobil = false }) {
  const fv = farve || STATUS.konfigureret;
  const safeDelta = (delta != null && isFinite(delta)) ? delta : null;
  const deltaPil   = safeDelta > 0 ? '↑' : safeDelta < 0 ? '↓' : null;
  const deltaFarve = safeDelta > 0 ? STATUS.live : safeDelta < 0 ? STATUS.fejl : TEKST.sekundær;
  const bf = badge ? (BADGE_FV[badge] || BADGE_FV['KONFIGURERET']) : null;
  const klikbar = !!onClick;

  // På mobil: kortere badge-tekst, ingen minWidth, lille primærtal
  const visBadge = badge
    ? (mobil ? (BADGE_MOBIL[badge] || badge) : badge)
    : null;

  // OS-09: når badge er til stede på mobil reserveres 42px til højre
  // så badge-absolut-element ikke overlapper primærtal.
  const badgePaddingRight = (mobil && bf && visBadge) ? 42 : undefined;

  const kortStyle = {
    position:    'relative',
    flex:        1,
    // Mobil: ingen minWidth — grid styrer bredden (2-kol)
    // Desktop: minWidth 160 bevares
    minWidth:    mobil ? 0 : 160,
    background:  BG.kort,
    border:      `1px solid ${KANT.standard}`,
    borderLeft:  `3px solid ${fv}`,
    borderRadius: RADIUS.kort,
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    padding:     mobil ? SPACING.kortPaddingTæt : SPACING.kortPadding,
    // OS-09: ekstra paddingRight på mobil når badge er til stede
    ...(badgePaddingRight != null ? { paddingRight: badgePaddingRight } : {}),
    cursor:      klikbar ? 'pointer' : 'default',
    // Hover-transform DEAKTIVERES på touch-enheder (ingen hover-intention)
    transition:  mobil ? 'none' : 'transform .15s, box-shadow .15s',
    fontFamily:  FONT.family,
    overflow:    'hidden',
    ...style,
  };

  return (
    <div
      style={kortStyle}
      onClick={onClick}
      // Hover-handlers sættes KUN på desktop (mobil=false)
      onMouseEnter={(!mobil && klikbar) ? e => {
        e.currentTarget.style.transform   = 'translateY(-2px)';
        e.currentTarget.style.boxShadow   = `0 8px 24px rgba(0,0,0,0.28)`;
      } : undefined}
      onMouseLeave={(!mobil && klikbar) ? e => {
        e.currentTarget.style.transform   = '';
        e.currentTarget.style.boxShadow   = '';
      } : undefined}
    >
      {/* Badge øverst til højre */}
      {bf && visBadge && (
        <span style={{
          position:    'absolute',
          top:         8,
          right:       8,
          ...TYPE.badge,
          padding:     '2px 5px',
          borderRadius: RADIUS.badge,
          background:  bf.bg,
          border:      `1px solid ${bf.kant}`,
          color:       bf.txt,
        }}>
          {visBadge}
        </span>
      )}

      {/* Ikon + Label */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: mobil ? 6 : 10 }}>
        {ikon && (
          <span style={{ fontSize: 14, lineHeight: 1, color: TEKST.sekundær, display: 'flex', alignItems: 'center', flexShrink: 0, marginTop: 1 }}>
            {ikon}
          </span>
        )}
        <span style={{
          ...TYPE.kpiLabel,
          color: TEKST.sekundær,
          // Mobil: tillad wrap på 2 linjer med passende line-height.
          // Desktop: white-space nowrap (uændret adfærd).
          whiteSpace:   mobil ? 'normal' : 'nowrap',
          lineHeight:   mobil ? 1.35 : 1,
          overflowWrap: mobil ? 'break-word' : 'normal',
          minWidth:     0,
        }}>
          {/* Brug kortLabel på mobil hvis tilgængeligt — ellers fuld label */}
          {mobil && kortLabel ? kortLabel : label}
        </span>
      </div>

      {/* Skeletonlinje */}
      {loading ? (
        <div style={{
          height: mobil ? 28 : 36, borderRadius: 6,
          background: 'linear-gradient(90deg, rgba(255,255,255,0.05) 25%, rgba(255,255,255,0.10) 50%, rgba(255,255,255,0.05) 75%)',
          backgroundSize: '200% 100%',
          animation: 'kpi-shimmer 1.4s infinite',
        }} />
      ) : (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          {/* Primærtal — ALTID hvidt (#f0f4f8) når der ER et tal.
              Tom-tilstand (null eller "—"): diskret streg i TEKST.sekundær,
              mindre og lettere, så det signalerer "afventer data" frem for et nul-resultat.
              Mobil: 28px (vs 36px desktop) — plads til 2-kol layout */}
          {(value == null || value === '—') ? (
            <span style={{ fontSize: mobil ? 22 : 26, fontWeight: 400, lineHeight: 1, color: TEKST.sekundær }}>
              —
            </span>
          ) : (
            // OS-09 fix: mobil bruger clamp(18px, 6.5vw, 28px) så lange værdier
            // ("174.000 kr", "60.000 kr") skaleres ned på smalle kort (~112px).
            // overflow+ellipsis er sikkerhedsnet mod ekstremt lange strenge.
            // minWidth:0 tillader flex-shrink. maxWidth:100% forhindrer overflyd.
            <span style={{
              ...TYPE.kpiTal,
              fontSize: mobil ? 'clamp(18px, 6.5vw, 28px)' : TYPE.kpiTal.fontSize,
              color: TEKST.primær,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
              maxWidth: '100%',
              display: 'block',
            }}>
              {value}
            </span>
          )}
          {deltaPil && (
            <span style={{ fontSize: 12, fontWeight: 700, color: deltaFarve }}>
              {deltaPil}{Math.abs(Math.round(safeDelta))}
              {deltaLabel && !mobil && (
                <span style={{ fontSize: 10, fontWeight: 400, color: TEKST.sekundær, marginLeft: 3 }}>
                  {deltaLabel}
                </span>
              )}
            </span>
          )}
        </div>
      )}

      {/* Pil-indikator for klikbar — skjules på mobil (platspres) */}
      {klikbar && !badge && !mobil && (
        <span style={{ position: 'absolute', bottom: 10, right: 12, fontSize: 11, color: TEKST.sekundær }}>
          →
        </span>
      )}
    </div>
  );
}
